import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Container } from "@/components/layout/Container";
import { Section } from "@/components/layout/Section";
import { Card, CardContent } from "@/components/ui/card";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Cookies() {
  useDocumentMeta({
    title: 'Cookie Policy',
    description: 'Understand how SurviAI uses cookies and how to manage your preferences.'
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <Section className="pt-24 pb-16">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-foreground mb-4">Cookie Policy</h1>
            <p className="text-muted-foreground mb-8">Last updated: January 2025</p>
            
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. What Are Cookies?</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Cookies are small text files that are placed on your computer or mobile device when you 
                  visit a website. They are widely used to make websites work more efficiently and provide 
                  information to the owners of the site. Cookies help us enhance your experience on SurviAI.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Types of Cookies We Use</h2>
                
                <Card className="mb-4">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Essential Cookies</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      These cookies are necessary for the website to function and cannot be switched off. 
                      They are usually set in response to actions made by you, such as setting your privacy 
                      preferences, logging in, or filling in forms.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>Examples:</strong> Session cookies, authentication tokens, CSRF tokens
                    </p>
                  </CardContent>
                </Card>

                <Card className="mb-4">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Performance Cookies</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      These cookies allow us to count visits and traffic sources so we can measure and 
                      improve the performance of our site. They help us understand which pages are the 
                      most and least popular and see how visitors move around the site.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>Examples:</strong> Analytics cookies, load balancing cookies
                    </p>
                  </CardContent>
                </Card>

                <Card className="mb-4">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Functional Cookies</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      These cookies enable enhanced functionality and personalization, such as remembering 
                      your preferences (like language or theme settings). They may be set by us or by 
                      third-party providers whose services we use.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>Examples:</strong> Theme preference, language preference, user settings
                    </p>
                  </CardContent>
                </Card>

                <Card className="mb-4">
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">Marketing Cookies</h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      These cookies may be set through our site by our advertising partners. They may be 
                      used to build a profile of your interests and show you relevant adverts on other sites. 
                      They do not store directly personal information but are based on uniquely identifying 
                      your browser and internet device.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <strong>Note:</strong> We currently do not use marketing cookies.
                    </p>
                  </CardContent>
                </Card>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. Cookies We Use</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-foreground font-semibold">Cookie Name</th>
                        <th className="text-left py-3 px-2 text-foreground font-semibold">Purpose</th>
                        <th className="text-left py-3 px-2 text-foreground font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border">
                        <td className="py-3 px-2">surviai-auth</td>
                        <td className="py-3 px-2">Authentication session</td>
                        <td className="py-3 px-2">Session</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-2">surviai-theme</td>
                        <td className="py-3 px-2">Theme preference (light/dark)</td>
                        <td className="py-3 px-2">1 year</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-2">surviai-consent</td>
                        <td className="py-3 px-2">Cookie consent preference</td>
                        <td className="py-3 px-2">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Third-Party Cookies</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In addition to our own cookies, we may also use various third-party cookies to report 
                  usage statistics of the Service and deliver advertisements on and through the Service.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Third-party services we may use include analytics providers and authentication services. 
                  These providers have their own privacy policies governing the use of cookies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Managing Cookies</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Most web browsers allow you to control cookies through their settings preferences. 
                  However, if you limit the ability of websites to set cookies, you may impact your 
                  overall user experience.
                </p>
                
                <h3 className="text-xl font-medium text-foreground mb-3 mt-6">Browser Settings</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You can manage cookies through your browser settings. Here are links to cookie 
                  management instructions for popular browsers:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Google Chrome
                    </a>
                  </li>
                  <li>
                    <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Mozilla Firefox
                    </a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Safari
                    </a>
                  </li>
                  <li>
                    <a href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Microsoft Edge
                    </a>
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Do Not Track</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Some browsers have a "Do Not Track" feature that lets you tell websites that you do not 
                  want to have your online activities tracked. We currently respond to Do Not Track signals 
                  by disabling non-essential cookies when detected.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Updates to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Cookie Policy from time to time in order to reflect changes to the 
                  cookies we use or for other operational, legal or regulatory reasons. Please revisit 
                  this page regularly to stay informed about our use of cookies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about our use of cookies, please contact us at{" "}
                  <a href="mailto:privacy@survi.ai" className="text-primary hover:underline">
                    privacy@survi.ai
                  </a>
                </p>
              </section>
            </div>
          </div>
        </Container>
      </Section>

      <Footer />
    </div>
  );
}
